import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { api } from "../../actions/api";
import CreateGroupModal from "./CreateGroupModal";

export default function EditGroupModal({ groupId, onGroupUpdated }) {
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGroupData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use the new API endpoint for group details
        const res = await api.get(`user/group/about/${groupId}`);

        if (!res.data?.data?.about) {
          throw new Error("Invalid group data structure");
        }

        const groupData = res.data.data.about;

        // Format the initial data according to the API response
        setInitialData({
          name: groupData.name || "",
          about: groupData.about || "",
          access_type: groupData.access_type || "Open Discussion",
          members:
            groupData.members?.map((member) => ({
              id: member.user?.id,
              slug: member.user?.slug,
              image: member.user?.image,
              full_name: member.user?.full_name,
              role: member.role,
            })) || [],
          image: groupData.image || null,
          is_pinned: groupData.is_pinned || false,
        });
      } catch (error) {
        console.error("Error fetching group data:", error);
        setError(error);
        toast.error("Failed to load group data");
      } finally {
        setLoading(false);
      }
    };

    if (groupId) {
      fetchGroupData();
    }
  }, [groupId]);

  const handleSubmit = async (formData) => {
    try {


      // Create a proper FormData object
      const form = new FormData();

      // Append all form fields
      form.append("name", formData.name);
      form.append("about", formData.about);

      // Ensure access_type is exactly as expected by the API
      const accessType =
        formData.access_type === "Open Discussion"
          ? "Open Discussion"
          : "Admin Only";
      form.append("access_type", accessType);

      // Handle members - ensure they're added as an array
      if (formData.members && formData.members.length > 0) {
        formData.members.forEach((member) => {
          // The API expects members[] with user IDs
          form.append("members[]", member.id || member);
        });
      }

      // Handle image upload
      if (formData.image && typeof formData.image !== "string") {
        form.append("image", formData.image);
      }

      // Add is_pinned if it exists
      if (formData.is_pinned !== undefined) {
        form.append("is_pinned", formData.is_pinned);
      }

      // Debug: Log what we're sending

      for (let pair of form.entries()) {

      }

      // Use the correct endpoint for updating group
      const response = await api.post(`user/tribe-group/${groupId}`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });


      toast.success("Group updated successfully!");

      if (onGroupUpdated) {
        onGroupUpdated();
      }

      // Close modal properly
      closeModal();
    } catch (error) {
      console.error("Error updating group:", error);
      console.error("Error response:", error.response?.data);

      // More detailed error handling
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        let errorMessage = "Failed to update group:\n";

        Object.keys(errors).forEach((field) => {
          errorMessage += `${field}: ${errors[field].join(", ")}\n`;
        });

        toast.error(errorMessage);
      } else {
        toast.error(error.response?.data?.message || "Failed to update group");
      }
    }
  };

  const closeModal = () => {
    const modal = document.getElementById("editGroupModal");
    if (modal) {
      // Use vanilla JS to hide the modal
      modal.classList.remove("show");
      modal.setAttribute("aria-hidden", "true");
      modal.style.display = "none";

      // Remove backdrop
      document.body.classList.remove("modal-open");
      const backdrop = document.querySelector(".modal-backdrop");
      if (backdrop) backdrop.remove();
    }
  };

  if (loading) {
    return (
      <div
        className="modal fade"
        id="editGroupModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2" style={{ color: "#fff"}}>Loading group details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="modal fade"
        id="editGroupModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Error</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body text-center py-4">
              <div className="text-danger mb-3">
                Failed to load group data. Please try again.
              </div>
              <button
                className="btn btn-primary"
                onClick={() => window.location.reload()}
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CreateGroupModal
      modalId="editGroupModal"
      isEditMode={true}
      initialData={initialData}
      onSubmit={handleSubmit}
      title="Edit Group"
      submitButtonText="Save Changes"
    />
  );
}
